import { useState, useMemo, useCallback, memo } from 'react';
import type { FC } from 'react';
import { Layout, Button, Card, Space, Typography, Tag, Descriptions, Switch } from 'antd';
import { CounterChartCard } from '../components/chart';
import { TonnesPerHourChartCard } from '../components/chart/TonnesPerHourChartCard';
import { AppHeader } from '@smolt/shared/components';
import type { ChartDataPoint } from '@smolt/shared/types';
import { useElementSize } from '@smolt/shared/hooks';
import {
    useSessionTimeseries,
    useLODStats,
    useTPHCalculation,
    useBaseCountOffset,
    useLiveDuration,
    type M4Bucket,
} from '../hooks/use-timeseries-lod';

/**
 * Live Duration Display Component
 * Isolated to prevent re-rendering parent component every second
 */
const LiveDurationDisplay: FC<{ sessionStart: number | null; isPlaying: boolean }> = memo(
    ({ sessionStart, isPlaying }) => {
        const duration = useLiveDuration(sessionStart, isPlaying);
        return <Typography.Text>Duration: {duration}</Typography.Text>;
    }
);

const { Title, Text } = Typography;

/**
 * CounterLabPage - 2-Stage LOD Test Page
 *
 * Demonstrates the 2-Stage LOD system:
 * Stage 1: Storage Level Selection (backend decides based on time range)
 * Stage 2: Pixel Downsample (M4) based on screen width
 */
export const CounterLabPage: FC = () => {
    // Lab State
    const [isPlaying, setIsPlaying] = useState(false);
    const [sessionStart, setSessionStart] = useState<number | null>(null);
    const [showDebug, setShowDebug] = useState(true);

    // Layout Measurement for Responsive Budget
    const { ref: chartContainerRef, size: chartContainerSize } = useElementSize();

    // 2-Stage LOD Data Fetching
    const {
        data: timeseriesData,
        isLoading,
        error,
        meta: { quantizedWidth, targetPoints, isMobile },
    } = useSessionTimeseries(sessionStart, chartContainerSize.width || 800, {
        enabled: isPlaying && sessionStart !== null,
        refetchInterval: 1000, // 1Hz polling
    });

    // LOD Stats for debugging
    const { data: lodStats } = useLODStats(showDebug);

    // Base Count Offset (for Y-axis starting at 0)
    // Pass sessionStart so the chart starts at 0 from session start time
    const baseCount = useBaseCountOffset(timeseriesData?.data, sessionStart);

    // TPH Calculation
    const windowMs = isMobile ? 1250 : 1000;
    const { tph: currentTPH } = useTPHCalculation(
        timeseriesData?.data,
        windowMs,
        1.45 // weight per fish in kg
    );

    // Convert M4 Buckets to ChartDataPoint format
    const chartData: ChartDataPoint[] = useMemo(() => {
        if (!timeseriesData?.data || timeseriesData.data.length === 0) {
            return sessionStart
                ? [{ time: '00:00', count: 0 }]
                : [];
        }

        // Filter buckets to only include data from session start onwards
        const buckets = sessionStart
            ? timeseriesData.data.filter((b: M4Bucket) => b.end_ts >= sessionStart)
            : timeseriesData.data;

        if (buckets.length === 0) {
            return sessionStart
                ? [{ time: '00:00', count: 0 }]
                : [];
        }

        const base = baseCount || 0;

        const mapped = buckets.map((b: M4Bucket) => {
            // Format Time: MM:SS relative to session start
            const elapsedSec = sessionStart
                ? Math.max(0, Math.floor((b.end_ts - sessionStart) / 1000))
                : 0;
            const mm = Math.floor(elapsedSec / 60);
            const ss = elapsedSec % 60;
            const timeStr = `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;

            // Offset the count (base is the count at session start)
            const val = b.last - base;

            return {
                time: timeStr,
                count: val < 0 ? 0 : val,
                timestamp: b.end_ts,
            };
        });

        // Ensure 00:00 at start with count 0
        if (mapped.length > 0 && mapped[0].time !== '00:00') {
            mapped.unshift({
                time: '00:00',
                count: 0,
                timestamp: sessionStart || Date.now(),
            });
        }

        return mapped;
    }, [timeseriesData?.data, baseCount, sessionStart]);

    // TPH Chart Data (windowed calculation)
    // densityFactor: 1.0 = full density, 0.5 = half density (fewer points)
    const TPH_DENSITY_FACTOR = 0.5; // TPH 차트 포인트를 절반으로 줄임

    const tphData: ChartDataPoint[] = useMemo(() => {
        if (chartData.length < 2) return [];

        const weightPerFishKg = 1.45;
        const result: ChartDataPoint[] = [];
        // densityFactor가 낮을수록 window가 커져서 포인트가 줄어듦
        const baseWindowMs = isMobile ? 1250 : 1000;
        const WINDOW_MS = baseWindowMs / TPH_DENSITY_FACTOR; // 0.5 → 2000ms window

        let windowStartIdx = 0;

        for (let i = 1; i < chartData.length; i++) {
            const startPoint = chartData[windowStartIdx] as ChartDataPoint & { timestamp?: number };
            const currPoint = chartData[i] as ChartDataPoint & { timestamp?: number };

            let dt = 0;
            if (currPoint.timestamp && startPoint.timestamp) {
                dt = currPoint.timestamp - startPoint.timestamp;
            } else {
                const [m1, s1] = currPoint.time.split(':').map(Number);
                const [m2, s2] = startPoint.time.split(':').map(Number);
                dt = (m1 * 60 + s1 - (m2 * 60 + s2)) * 1000;
            }

            if (dt >= WINDOW_MS || (i === chartData.length - 1 && dt >= WINDOW_MS * 0.5)) {
                const dCount = currPoint.count - startPoint.count;
                const dtSec = dt / 1000.0;
                const rate = dCount / dtSec;
                const tph = (rate * weightPerFishKg * 3600) / 1000;

                result.push({
                    time: currPoint.time,
                    count: tph,
                });

                windowStartIdx = i;
            }
        }

        return result;
    }, [chartData, isMobile]);

    // TPH Stats
    const avgTPH =
        tphData.length > 0
            ? tphData.reduce((acc, p) => acc + p.count, 0) / tphData.length
            : 0;
    const peakTPH =
        tphData.length > 0 ? Math.max(...tphData.map((p) => p.count)) : 0;

    // Session Stats - only updates when chart data changes
    const currentStats = useMemo(() => {
        const last = chartData[chartData.length - 1];
        const count = last ? last.count : 0;
        const weightAvg = 1.45;
        const weight = (count * weightAvg) / 1000;

        return {
            count: Math.floor(count),
            weight,
        };
    }, [chartData]);

    // Controls
    const handleStart = useCallback(() => {
        if (!isPlaying) {
            if (!sessionStart) {
                setSessionStart(Date.now());
            }
            setIsPlaying(true);
        } else {
            setIsPlaying(false);
        }
    }, [isPlaying, sessionStart]);

    const handleReset = useCallback(() => {
        setIsPlaying(false);
        setSessionStart(null);
    }, []);

    // LOD Level Color
    const getLevelColor = (level: number) => {
        const colors = ['green', 'blue', 'cyan', 'orange', 'red', 'purple'];
        return colors[level] || 'default';
    };

    return (
        <Layout style={{ minHeight: '100vh', background: '#3B3B3D' }}>
            <AppHeader
                title="2-Stage LOD Chart Lab"
                showBackButton={true}
                onBackClick={() => (window.location.href = '/')}
            />

            <div
                style={{
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    width: '100%',
                }}
            >
                {/* Controls Card */}
                <Card title="Backend Controls" size="small">
                    <div style={{ marginBottom: 10 }}>
                        <Text type="secondary">
                            Connects to <Text code>localhost:8000</Text> with 2-Stage LOD:
                        </Text>
                        <br />
                        <Text type="secondary">
                            Stage 1: Storage Level (time range) → Stage 2: Pixel Downsample (M4)
                        </Text>
                    </div>

                    <Space wrap>
                        <Button type="primary" onClick={handleStart} loading={isLoading}>
                            {isPlaying ? 'Pause' : 'Start Fetching'}
                        </Button>
                        <Button onClick={handleReset}>Reset Session</Button>
                        <Text strong>Count: {currentStats.count.toLocaleString()}</Text>
                        <LiveDurationDisplay sessionStart={sessionStart} isPlaying={isPlaying} />
                        <Switch
                            checked={showDebug}
                            onChange={setShowDebug}
                            checkedChildren="Debug ON"
                            unCheckedChildren="Debug OFF"
                        />
                    </Space>

                    {error && (
                        <div style={{ marginTop: 10, color: 'red' }}>
                            Error: {error.message}
                        </div>
                    )}
                </Card>

                {/* Debug Panel */}
                {showDebug && timeseriesData && (
                    <Card title="2-Stage LOD Debug" size="small">
                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="Stage 1: Storage Level">
                                <Tag color={getLevelColor(timeseriesData.stage1_level)}>
                                    L{timeseriesData.stage1_level}
                                </Tag>
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                    {timeseriesData.stage1_desc}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Stage 2: Pixel Budget">
                                <Tag color="blue">{timeseriesData.stage2_target} points</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Fetched Buckets">
                                <Tag>{timeseriesData.count}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Quantized Width">
                                <Tag color={isMobile ? 'orange' : 'green'}>
                                    {quantizedWidth}px ({isMobile ? 'Mobile' : 'Desktop'})
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Target Points">
                                <Tag color="purple">{targetPoints}</Tag>
                            </Descriptions.Item>
                        </Descriptions>

                        {/* LOD Storage Stats */}
                        {lodStats && (
                            <div style={{ marginTop: 16 }}>
                                <Title level={5}>Storage Levels</Title>
                                <Space wrap>
                                    {Object.entries(lodStats.levels).map(([level, stats]) => (
                                        <Tag
                                            key={level}
                                            color={
                                                stats.count > 0
                                                    ? getLevelColor(parseInt(level.replace('L', '')))
                                                    : 'default'
                                            }
                                        >
                                            {level}: {stats.count} buckets ({stats.interval_ms}ms)
                                        </Tag>
                                    ))}
                                </Space>
                            </div>
                        )}
                    </Card>
                )}

                {/* Charts */}
                <div
                    ref={chartContainerRef}
                    style={{
                        background: '#4A4A4C',
                        padding: '20px',
                        borderRadius: '8px',
                    }}
                >
                    <Title level={4} style={{ color: 'white', marginBottom: '10px' }}>
                        Live Chart (W={chartContainerSize.width}px → Q={quantizedWidth}px)
                    </Title>

                    <div className="grid grid-cols-1 gap-4 mb-4">
                        <CounterChartCard
                            data={chartData}
                            currentCount={currentStats.count}
                            weight={currentStats.weight}
                            weightPerFish={1.45}
                            tonnesPerHour={currentTPH}
                            sessionStart={sessionStart}
                            isCounting={isPlaying}
                        />
                        <TonnesPerHourChartCard
                            data={tphData}
                            currentTPH={currentTPH}
                            averageTPH={avgTPH}
                            peakTPH={peakTPH}
                            targetTPH={50.0}
                            isCounting={isPlaying}
                        />
                    </div>
                </div>

                {/* LOD Policy Reference */}
                <Card title="LOD Policy Reference" size="small">
                    <Descriptions bordered size="small" column={1}>
                        <Descriptions.Item label="L1 (100ms)">
                            {'<5min: Finest level (10Hz)'}
                        </Descriptions.Item>
                        <Descriptions.Item label="L2 (1s)">
                            {'<30min: 1Hz aggregated'}
                        </Descriptions.Item>
                        <Descriptions.Item label="L3 (5s)">
                            {'<2h: Flexibility level'}
                        </Descriptions.Item>
                        <Descriptions.Item label="L4 (10s)">
                            {'<12h: DEFAULT query level'}
                        </Descriptions.Item>
                        <Descriptions.Item label="L5 (1m)">
                            {'>=12h: Long-term storage'}
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
            </div>
        </Layout>
    );
};
